import { Status as ItemStatus } from '../claim-item/status';

export type ReadinessField = 'lossDate' | 'usageAtLoss' | 'items' | 'totals' | 'isZeroPayConfirmed' | 'payeeType';

/** A blocker, tied to the field to fix. */
export interface ReadinessIssue {
  field: ReadinessField;
  message: string;
}

export interface ReadinessInput {
  lossDate: Date | string | null;
  usageAtLoss: { value: number | null } | null;
  items: { status: ItemStatus; deleted?: Date | null }[];
  /** Null when the claim has no charges. */
  totals: { netPayable: number } | null;
  /** The asset has a usage reading (odometer, engine hours…); a phone doesn't. */
  isUsageAtLossRequired: boolean;
  /** Tenant setting: allow $0 authorizations. */
  allowZeroPay?: boolean;
}

/** Jobs that count towards payment. */
function isAuthorizedJob(status: ItemStatus) {
  return status === ItemStatus.APPROVED || status === ItemStatus.RESOLVED;
}

export function getReadinessIssues(claim: ReadinessInput): ReadinessIssue[] {
  const issues: ReadinessIssue[] = [];

  if (!claim.lossDate) issues.push({ field: 'lossDate', message: 'Enter the date of loss.' });
  if (claim.isUsageAtLossRequired && claim.usageAtLoss?.value == null) {
    issues.push({ field: 'usageAtLoss', message: 'Enter the usage at loss.' });
  }

  const jobs = claim.items.filter(item => !item.deleted);

  const pending = jobs.filter(job => job.status === ItemStatus.DRAFT).length;
  if (pending) {
    issues.push({
      field: 'items',
      message: `${pending} repair item${pending === 1 ? ' still needs' : 's still need'} a decision.`,
    });
  }

  const hasAuthorizedJob = jobs.some(job => isAuthorizedJob(job.status));
  if (!claim.allowZeroPay && !hasAuthorizedJob) {
    issues.push({ field: 'items', message: 'Approve at least one repair item.' });
  }

  if (!claim.totals) {
    issues.push({ field: 'totals', message: 'Add the repair charges before authorizing.' });
  } else if (!claim.allowZeroPay && hasAuthorizedJob && claim.totals.netPayable === 0) {
    // e.g. the deductible covers the approved repairs.
    issues.push({ field: 'totals', message: 'The net payable is $0, and zero-pay authorization is turned off.' });
  }

  return issues;
}
